import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Copy, Check, Code2 } from 'lucide-react'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { courseEmbedSnippet, courseEmbedUrl, type CourseEmbedMode, type CourseEmbedStyle } from './embedSnippet'

const LIMITS = [3, 6, 9, 12]

/** Choose what to show, see it, and copy the code that puts it on the customer's own website. Pro plan only. */
export default function CourseEmbedModal({ onClose, initialCourseSlug }: { onClose: () => void; initialCourseSlug?: string }) {
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: categories = [] } = useQuery({
    queryKey: ['course-categories'],
    queryFn: () => adminApi.getCourseCategories().then(r => r.data),
  })
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const live = useMemo(
    () => courses.filter(c => c.showOnWebsite && c.slug).sort((a, b) => a.name.localeCompare(b.name)),
    [courses],
  )

  const [mode, setMode] = useState<CourseEmbedMode>(initialCourseSlug ? 'course' : 'catalogue')
  const [courseSlug, setCourseSlug] = useState(initialCourseSlug ?? '')
  const [categoryId, setCategoryId] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [limit, setLimit] = useState<number | null>(null)
  const [ownColour, setOwnColour] = useState(false)
  const [colour, setColour] = useState('#4f46e5')
  const [style, setStyle] = useState<CourseEmbedStyle>('frame')
  const [linkTemplate, setLinkTemplate] = useState('')
  const [copied, setCopied] = useState(false)

  const tenantSlug = profile?.slug ?? ''
  const brandColour = profile?.secondaryColour || profile?.primaryColour || '#4f46e5'
  // The first live course until one is chosen, so the preview always shows something real.
  const chosenCourse = live.find(c => c.slug === courseSlug) ?? live[0]

  const options = {
    origin: window.location.origin,
    tenantSlug,
    mode,
    courseSlug: chosenCourse?.slug ?? undefined,
    category: categories.find(c => c.id === categoryId)?.slug ?? null,
    courses: picked,
    limit,
    colour: ownColour ? colour : null,
    linkTemplate,
  }
  const url = courseEmbedUrl(options)
  const snippet = courseEmbedSnippet(options, style)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch { /* the code is on screen, so it can be copied by hand */ }
  }
  const toggle = (slug: string) => setPicked(p => (p.includes(slug) ? p.filter(s => s !== slug) : [...p, slug]))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div role="dialog" aria-label="Embed courses on your website" className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Code2 size={18} className="text-indigo-500" />
            <h2 className="font-semibold text-slate-900">Embed courses on your website</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 cursor-pointer"><X size={18} /></button>
        </div>

        {live.length === 0 ? (
          <div className="space-y-2 p-6 text-sm text-slate-600">
            <p className="font-medium text-slate-900">No courses are switched on yet.</p>
            <p>Open a course, go to its Website tab and tick "Show this course on my website". It will then appear here.</p>
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
            <div className="shrink-0 space-y-5 overflow-y-auto border-b border-slate-100 p-5 lg:w-64 lg:border-b-0 lg:border-r">
              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">How to add it</legend>
                {([['frame', 'Frame', 'Works on any website'], ['script', 'In your page', 'Uses your fonts and styles, and search engines can read it']] as const).map(([value, label, hint]) => (
                  <label key={value} className="mb-2 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                    <input type="radio" name="embed-style" className="mt-0.5" checked={style === value} onChange={() => setStyle(value)} />
                    <span>{label}<span className="block text-xs text-slate-400">{hint}</span></span>
                  </label>
                ))}
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">What to show</legend>
                {([['catalogue', 'All my courses'], ['course', 'One course']] as const).map(([value, label]) => (
                  <label key={value} className="mb-1.5 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="embed-mode" checked={mode === value} onChange={() => setMode(value)} />
                    {label}
                  </label>
                ))}
              </fieldset>

              {mode === 'course' ? (
                <div>
                  <label htmlFor="embed-course" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Course</label>
                  <select id="embed-course" value={chosenCourse?.slug ?? ''} onChange={e => setCourseSlug(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
                    {live.map(c => <option key={c.id} value={c.slug!}>{c.name}</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="embed-category" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Category</label>
                    <select id="embed-category" value={categoryId} onChange={e => setCategoryId(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
                      <option value="">All categories</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Only these courses</p>
                    <div className="max-h-36 space-y-1.5 overflow-y-auto">
                      {live.map(c => (
                        <label key={c.id} className="flex cursor-pointer items-start gap-2 text-sm text-slate-600">
                          <input type="checkbox" className="mt-0.5 shrink-0 rounded" checked={picked.includes(c.slug!)} onChange={() => toggle(c.slug!)} />
                          <span>{c.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">Leave all unticked to show every course.</p>
                  </div>
                  {style === 'script' && (
                    <div>
                      <label htmlFor="embed-link" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Link cards to (optional)</label>
                      <input id="embed-link" type="text" value={linkTemplate} onChange={e => setLinkTemplate(e.target.value)} placeholder="/courses/{slug}"
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                      <p className="mt-1 text-xs text-slate-400">A page on your own website for each course, where {'{slug}'} becomes the course's web address. Leave blank to link to the CertSlate course page.</p>
                    </div>
                  )}
                  <div>
                    <label htmlFor="embed-limit" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Show up to</label>
                    <select id="embed-limit" value={limit ?? ''} onChange={e => setLimit(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
                      <option value="">All of them</option>
                      {LIMITS.map(n => <option key={n} value={n}>{n} courses</option>)}
                    </select>
                  </div>
                </>
              )}

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Accent colour</p>
                <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="rounded" checked={!ownColour}
                    onChange={e => { setOwnColour(!e.target.checked); if (!e.target.checked) setColour(brandColour) }} />
                  Use my brand colour
                  <span className="h-4 w-4 rounded-full border border-slate-200" style={{ backgroundColor: brandColour }} aria-hidden="true" />
                </label>
                {ownColour && (
                  <div className="flex items-center gap-2">
                    <input type="color" aria-label="Accent colour" value={colour} onChange={e => setColour(e.target.value)}
                      className="h-8 w-12 cursor-pointer rounded border border-slate-200 p-0.5" />
                    <span className="font-mono text-sm text-slate-500">{colour}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-1 flex-col space-y-4 overflow-y-auto p-5">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</p>
                {tenantSlug ? (
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                    <iframe key={url} src={url} title="Embed preview" style={{ width: '100%', height: 340, border: 0, display: 'block' }} />
                  </div>
                ) : (
                  <div className="flex h-24 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-400">No company address configured</div>
                )}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Embed code</p>
                <pre className="overflow-auto whitespace-pre rounded-lg bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100" data-testid="embed-snippet">{snippet}</pre>
                <p className="mt-2 text-xs text-slate-500">
                  {style === 'frame'
                    ? 'Paste both lines where you want the courses to appear. The second line makes the frame fit its content, so visitors never see a scrollbar inside it.'
                    : 'Paste both lines where you want the courses to appear. The script only needs to be on the page once, even with several courses. The preview above shows the frame version; in your page the courses take on your own fonts, and you can restyle them with --cs-accent, --cs-radius and --cs-font.'}
                </p>
              </div>
              <Button onClick={copy} className="w-full shrink-0">
                {copied ? <><Check size={15} className="mr-1.5" /> Copied!</> : <><Copy size={15} className="mr-1.5" /> Copy code</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
